import React, { useState } from "react";
import { stockAPI } from "../services/api";
import { useI18n } from "../i18n";
import Modal from "./Modal";

/**
 * Moves one product's stock between the shop and the warehouse. The register
 * sells only the shelf, so moving stock to the shop is what makes it sellable,
 * and moving it to the warehouse takes it off sale.
 */
const TransferDialog = ({ row, name, initialTo, onClose, onDone }) => {
  const { t } = useI18n();
  const [to, setTo] = useState(initialTo || (row.warehouse_quantity > 0 ? "shop" : "warehouse"));
  const [quantity, setQuantity] = useState(
    row.suggested_move && (initialTo || "shop") === "shop" ? String(row.suggested_move) : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const available = to === "warehouse" ? row.stock_quantity : row.warehouse_quantity;
  const amount = /^\d+$/.test(quantity.trim()) ? Number(quantity.trim()) : NaN;
  const valid = amount > 0 && amount <= available;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;

    setSaving(true);
    setError("");

    try {
      await stockAPI.transfer([{ product_id: row.id, to, quantity: amount }]);
      onDone({ name, to, quantity: amount });
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("stock.transfer.failed"));
      setSaving(false);
    }
  };

  return (
    <Modal title={t("stock.transfer.title")} onClose={onClose} maxWidthClass="max-w-sm">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <p className="font-semibold text-gray-800">{name}</p>
          <p className="text-sm text-gray-500">
            {t("stock.transfer.now", { shop: row.stock_quantity, warehouse: row.warehouse_quantity })}
          </p>
        </div>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div role="group" aria-label={t("stock.transfer.direction")} className="grid grid-cols-2 gap-2">
          {["shop", "warehouse"].map((target) => (
            <button
              key={target}
              type="button"
              aria-pressed={to === target}
              onClick={() => {
                setTo(target);
                setQuantity("");
              }}
              className={`py-2 rounded-lg text-sm font-semibold border ${
                to === target
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {t(`stock.transfer.to.${target}`)}
            </button>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("stock.transfer.quantity", { max: available })}
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              max={available}
              step="1"
              inputMode="numeric"
              autoFocus
              dir="ltr"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="flex-1 px-4 py-3 text-xl text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="button"
              disabled={available <= 0}
              onClick={() => setQuantity(String(available))}
              className="px-4 rounded-lg bg-gray-100 hover:bg-gray-200 font-semibold text-gray-700 disabled:opacity-40"
            >
              {t("stock.transfer.all")}
            </button>
          </div>
          {amount > available && (
            <p className="mt-2 text-sm text-red-700">{t("stock.transfer.tooMany", { max: available })}</p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!valid || saving}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {t("stock.transfer.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TransferDialog;
